import * as React from 'react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Textarea,
} from '~/components/ui/primitives';

/**
 * Confirmation d'une action irreversible.
 *
 * `requireReason` impose la saisie d'un motif : c'est ce qui alimente le champ
 * `cancellationReason` d'une intervention, exige par la contrainte de coherence
 * posee en base. Le bouton reste inactif tant que le motif est trop court, plutot
 * que d'echouer cote serveur apres coup.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'default',
  requireReason = false,
  reasonLabel = 'Motif',
  reasonPlaceholder,
  loading = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  loading?: boolean;
  onConfirm: (reason?: string) => void | Promise<void>;
}) {
  const [reason, setReason] = React.useState('');

  // Le motif est remis a zero a chaque ouverture : sans cela, le motif d'une
  // annulation precedente serait pre-rempli sur la suivante.
  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const reasonTooShort = requireReason && reason.trim().length < 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {requireReason ? (
          <Field
            label={reasonLabel}
            htmlFor="confirm-reason"
            required
            hint="Ce motif est conserve dans l'historique de l'intervention."
          >
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={reasonPlaceholder ?? 'Precisez la raison…'}
              rows={3}
              maxLength={500}
            />
          </Field>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            loading={loading}
            disabled={reasonTooShort}
            onClick={() => void onConfirm(requireReason ? reason.trim() : undefined)}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
